import { useMemo } from 'react';
import { getPlanI18nSlug, useHomepageText, type PlanI18nSlug } from './homepageI18n';
import {
  UNIFIED_PLAN_PRICES_TZS,
  UNIFIED_PLAN_PRICES_USD,
  formatPlanPrice,
  isPaidUnifiedPlan,
  type PaidUnifiedPlanKey,
} from '../config/unifiedPlans';

export type PricingPlan = {
  name: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonColor: string;
  isPopular: boolean;
  id?: string;
};

function localizedMonthlyPrice(slug: PlanI18nSlug | null, lang: string): string | null {
  if (!slug || slug === 'free' || !isPaidUnifiedPlan(slug)) return null;
  const key = slug as PaidUnifiedPlanKey;
  if (lang === 'sw') {
    return formatPlanPrice(UNIFIED_PLAN_PRICES_TZS[key], 'TZS');
  }
  return formatPlanPrice(UNIFIED_PLAN_PRICES_USD[key], 'USD');
}

/** Localize subscription plans from API/CMS using home_plan_{slug}_* keys. */
export function useLocalizedPricingPlans(plans: PricingPlan[] | undefined | null) {
  const { txt, txtList, lang } = useHomepageText();
  const cms = plans || [];

  return useMemo(() => {
    return cms.map((plan) => {
      const slug = getPlanI18nSlug(plan.name || '');
      if (!slug) return plan;
      const prefix = `home_plan_${slug}`;
      const price = localizedMonthlyPrice(slug, lang) ?? plan.price;
      return {
        ...plan,
        name: txt(`${prefix}_name`, plan.name),
        description: txt(`${prefix}_desc`, plan.description),
        buttonText: txt(`${prefix}_button`, plan.buttonText),
        features: txtList(`${prefix}_features`, plan.features),
        price,
      };
    });
  }, [cms, txt, txtList, lang]);
}
